import { type ComponentProps, memo, type ReactNode } from 'react';

import { tailwindVariants, type VariantProps } from '@air/utils-tailwind';

export const banner = tailwindVariants({
  base: 'flex gap-2 rounded-4',
  defaultVariants: {
    appearance: 'quiet',
    size: 'medium',
  },
  variants: {
    appearance: {
      danger: 'ads:bg-error-subtle bg-red-3 ads:text-error text-red-11',
      info: 'ads:bg-primary-subtle bg-blue-2 ads:text-accent text-blue-12',
      quiet: 'ads:bg-subtle bg-gray-3 ads:text-primary text-gray-12',
      success: 'ads:bg-success bg-teal-5 ads:text-primary text-blue-12',
      warning: 'ads:bg-warning bg-yellow-2 ads:text-warning text-yellow-12',
    },
    size: {
      medium: 'p-4 text-14',
      small: 'p-2 text-14',
    },
  },
});

export type BannerVariants = VariantProps<typeof banner>;

export type BannerProps = Omit<ComponentProps<'div'>, 'prefix'> &
  BannerVariants & {
    prefix?: ReactNode;
    suffix?: ReactNode;
  };

export const Banner = memo(({ appearance, children, className, prefix, size, suffix, ...restOfProps }: BannerProps) => {
  return (
    <div className={banner({ appearance, class: className, size })} data-testid="BANNER" role="alert" {...restOfProps}>
      {prefix ? <div className="shrink-0">{prefix}</div> : null}

      <div className="grow">{children}</div>

      {suffix ? <div className="shrink-0">{suffix}</div> : null}
    </div>
  );
});

Banner.displayName = 'Banner';
